import React from 'react';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import BudgetPhase from 'routes/BudgetModule/components/shared/BudgetLibrary/BudgetPhase';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { SHADOW } from 'styles/theme';
import FinanceSoftwareOverrideSelector from 'routes/BudgetModule/components/shared/BudgetHeader/FinanceSoftwareOverride';
import { useShouldDisplayDemoFinanceSoftwareSelector } from 'routes/BudgetModule/components/shared/BudgetHeader/hooks';
import BudgetBreadcrumbs from './BudgetBreadcrumbs';
import ResourceLocker from '../Locker/ResourcelLocker';
import KeyDatesHeaderWidget from 'routes/BudgetModule/components/shared/BudgetHeader/KeyDatesHeaderWidget';
import Flex from 'ui/Flex/Flex';

type BudgetHeaderProps = {
    headerTitle: string;
    budgetProposal?: BudgetProposalT;
    parentLink?: string;
    resourceName?: string;
    className?: string;
    resourceSoftwareId?: string;
    shouldUseLighterShadow?: boolean;
    canShowFinanceSoftwareSelector?: boolean;
    exercice: string | number | null;
};

const StyBudgetHeader = styled(Box, {
    name: 'StyBudgetHeader',
    shouldForwardProp: (prop) => !['shouldUseLighterShadow'].includes(prop),
})<{ shouldUseLighterShadow: boolean }>(
    ({ shouldUseLighterShadow, theme }) => ({
        position: 'sticky',
        top: 0,
        zIndex: 2,
        width: '100%',
        paddingLeft: '1.5rem',
        background: '#ffffff',
        boxShadow: shouldUseLighterShadow
            ? '4px 8px 24px rgba(0, 0, 0, 0.06)'
            : SHADOW,

        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '78px',
        minHeight: '78px',
        '& h1': {
            width: '100%',
        },
        gap: theme.spacing(2),
    }),
);

function BudgetHeader({
    budgetProposal = undefined,
    parentLink = '',
    resourceName = '',
    resourceSoftwareId = undefined,
    headerTitle,
    canShowFinanceSoftwareSelector = false,
    shouldUseLighterShadow = false,
    className = '',
    exercice,
}: BudgetHeaderProps): React.ReactElement {
    const name = budgetProposal?.name ?? '';
    const phase = budgetProposal?.phase ?? '';
    const shouldDisplayFinanceSoftwareSelector =
        useShouldDisplayDemoFinanceSoftwareSelector(
            canShowFinanceSoftwareSelector,
        );
    const shouldDisplayPhase = parentLink && phase;
    const shouldDisplayLocker = resourceSoftwareId && budgetProposal;
    return (
        <StyBudgetHeader
            className={className}
            shouldUseLighterShadow={shouldUseLighterShadow}
        >
            <Flex alignItems="center" gap={2} height="100%">
                <BudgetBreadcrumbs
                    headerTitle={headerTitle}
                    parentLink={parentLink}
                    parentTitle={name}
                    childTitle={resourceName}
                />
                {shouldDisplayPhase && (
                    <BudgetPhase phase={phase} sx={{ mr: 0 }} />
                )}
                {shouldDisplayLocker && (
                    <ResourceLocker
                        resourceSoftwareId={resourceSoftwareId}
                        rights={budgetProposal.canResourceEdit}
                        by={budgetProposal.by}
                    />
                )}
                {shouldDisplayFinanceSoftwareSelector && (
                    <FinanceSoftwareOverrideSelector sx={{ ml: 'auto' }} />
                )}
            </Flex>
            <KeyDatesHeaderWidget exercice={exercice} />
        </StyBudgetHeader>
    );
}

export default BudgetHeader;
